import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, ClipboardList } from 'lucide-react';

/**
 * PublicSurveyPage — what a family or staff member sees at /survey/:token.
 *
 * Accepts either the shared survey link or a personal invite token. Styled as a
 * public form, not an admin screen: one centred card, one question per block,
 * generous spacing, no OL chrome. No account needed.
 */
const CHOICE = ['dropdown', 'multiple_choice', 'radio'];

export default function PublicSurveyPage() {
  const { token } = useParams();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api/surveys/public/${token}`;

  const [s, setS] = useState(null);
  const [answers, setAnswers] = useState({});
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [name, setName] = useState('');
  const [personalized, setPersonalized] = useState(false);

  useEffect(() => {
    axios.get(API)
      .then(({ data }) => { setS(data.survey); setPersonalized(!!data.personalized); })
      .catch(e => setErr(e.response?.data?.detail || 'This survey link is not valid.'))
      .finally(() => setLoading(false));
  }, [API]);

  const set = (id, v) => setAnswers(a => ({ ...a, [id]: v }));

  const toggle = (id, opt) => setAnswers(a => {
    const cur = Array.isArray(a[id]) ? a[id] : [];
    return { ...a, [id]: cur.includes(opt) ? cur.filter(x => x !== opt) : [...cur, opt] };
  });

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await axios.post(API, {
        answers,
        respondent: (!s.anonymous && name.trim()) ? { name: name.trim() } : undefined,
      });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not submit — please try again.');
    } finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-400">OmniLeagues</div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7">{children}</div>
      </div>
    </div>
  );

  if (loading) return (
    <Shell><div className="flex items-center justify-center gap-2 text-slate-500 py-10">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div></Shell>
  );

  if (err && !s) return (
    <Shell>
      <h1 className="text-lg font-semibold text-slate-900">This survey isn’t available</h1>
      <p className="text-sm text-slate-500 mt-2">{err}</p>
    </Shell>
  );

  if (done) return (
    <Shell>
      <div className="text-center py-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Check className="h-6 w-6 text-emerald-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900">Thank you</h1>
        <p className="text-sm text-slate-500 mt-2">Your response has been recorded.</p>
      </div>
    </Shell>
  );

  const input = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500";

  return (
    <Shell>
      <div className="flex items-start gap-3 pb-5 border-b border-slate-100">
        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
          <ClipboardList className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900 leading-tight">{s.title}</h1>
          {s.description && <p className="text-sm text-slate-500 mt-1">{s.description}</p>}
        </div>
      </div>

      {s.anonymous && (
        <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mt-4">
          Your answers are anonymous — your name is not stored with them.
        </p>
      )}

      <div className="space-y-6 mt-6">
        {(s.questions || []).map((q, i) => (
          <div key={q.id} data-testid={`sq-${q.id}`}>
            <label className="block text-sm font-medium text-slate-900">
              {i + 1}. {q.label}
              {q.required && <span className="text-amber-600 ml-1">*</span>}
            </label>
            {q.help_text && <p className="text-xs text-slate-500 mt-0.5">{q.help_text}</p>}

            <div className="mt-2">
              {q.qtype === 'long_text' ? (
                <textarea rows={3} className={input} value={answers[q.id] || ''}
                  onChange={e => set(q.id, e.target.value)} />
              ) : CHOICE.includes(q.qtype) ? (
                <select className={input} value={answers[q.id] || ''}
                  onChange={e => set(q.id, e.target.value)}>
                  <option value="">Choose…</option>
                  {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : q.qtype === 'checkbox' ? (
                <div className="space-y-1.5">
                  {(q.options || []).map(o => (
                    <label key={o} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox"
                        checked={Array.isArray(answers[q.id]) && answers[q.id].includes(o)}
                        onChange={() => toggle(q.id, o)} />
                      {o}
                    </label>
                  ))}
                </div>
              ) : (
                <input className={input}
                  type={q.qtype === 'number' ? 'number' : q.qtype === 'date' ? 'date' : 'text'}
                  value={answers[q.id] || ''} onChange={e => set(q.id, e.target.value)} />
              )}
            </div>
          </div>
        ))}
      </div>

      {!s.anonymous && !personalized && (
        <div className="mt-6">
          <label className="block text-sm font-medium text-slate-900">Your name</label>
          <input className={`${input} mt-2`} value={name} onChange={e => setName(e.target.value)}
            placeholder="Optional" data-testid="sq-name" />
        </div>
      )}

      <button onClick={submit} disabled={busy}
        className="mt-7 w-full rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-40
                   text-white font-medium py-3 flex items-center justify-center gap-2"
        data-testid="sq-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        Submit
      </button>

      {err && <p className="text-sm text-red-600 mt-4">{err}</p>}
    </Shell>
  );
}
